import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { ErrorMessage, Field, Formik, Form as FormikForm } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { debounce } from "lodash";
import Spinner from "../components/Common/Spinner";
import Pagination from "../components/Pagination";
import {
  addLegalFaq,
  deleteLegalFaq,
  getLegalInfo,
  updateLegalFaq,
} from "../../services/Admin/adminServices";

const validationSchema = Yup.object().shape({
  question: Yup.string().required("Question is required"),
  answer: Yup.string().required("Answer is required"),
});

const parseLegalInfo = (response) => {
  const data = response?.data?.data;
  if (!data) return { legalInfoId: "", faqs: [] };

  if (Array.isArray(data)) {
    if (data[0]?.question) return { legalInfoId: "", faqs: data };
    const document = data[0] || {};
    return { legalInfoId: document._id || "", faqs: document.faq || [] };
  }

  return {
    legalInfoId: data._id || data.legalInfoId || "",
    faqs: Array.isArray(data.faq) ? data.faq : [],
  };
};

const LegalInfo = () => {
  const [legalInfoId, setLegalInfoId] = useState("");
  const [faq, setFaq] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedFaq, setSelectedFaq] = useState(null);
  const [itemsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const fetchFaqs = async () => {
    try {
      setLoading(true);
      const response = await getLegalInfo();
      const parsed = parseLegalInfo(response);
      setLegalInfoId(parsed.legalInfoId);
      setFaq(parsed.faqs);
    } catch (error) {
      console.error("error", error);
      toast.error(error?.response?.data?.message || "Failed to load legal information");
      setFaq([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedSearch(search);
      setCurrentPage(0);
    }, 500);

    handler();

    return () => {
      handler.cancel();
    };
  }, [search]);

  const filteredFaqs = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();
    if (!query) return faq;
    return faq.filter((item) => {
      const question = (item.question || "").toLowerCase();
      const answer = (item.answer || "").toLowerCase();
      return question.includes(query) || answer.includes(query);
    });
  }, [faq, debouncedSearch]);

  const totalItems = filteredFaqs.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const pageFaqs = filteredFaqs.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const handleDelete = async (id, index) => {
    try {
      await deleteLegalFaq(id, legalInfoId);
      setFaq((prev) => prev.filter((item) => item._id !== id));
      if (activeIndex === index) {
        setActiveIndex(null);
      } else if (activeIndex > index) {
        setActiveIndex(activeIndex - 1);
      }
      toast.success("Deleted SuccessFully");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error?.response?.data?.message || "Failed to delete FAQ");
    }
  };

  const handleEdit = (item) => {
    setSelectedFaq(item);
    setShowModal(true);
  };

  const handleAdd = () => {
    setSelectedFaq(null);
    setShowModal(true);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const payload = {
      question: values.question.trim(),
      answer: values.answer.trim(),
    };
    if (legalInfoId) payload.legalInfoId = legalInfoId;

    try {
      if (selectedFaq?._id) {
        await updateLegalFaq({ ...payload, faqId: selectedFaq._id });
        toast.success("Updated");
      } else {
        await addLegalFaq(payload);
        toast.success("FAQ added successfully!");
      }
      setShowModal(false);
      setSelectedFaq(null);
      fetchFaqs();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save FAQ");
      console.error("error->", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h2 className="mb-3" style={{ color: "#0AB7EA" }}>
        Legal Information
      </h2>
      <div className="d-flex gap-3 mb-3">
        <div className="flex-grow-1 ">
          <input
            type="text"
            style={{ borderRadius: "0.5rem", borderColor: "#0AB7EA" }}
            className="form-control w-100"
            placeholder="Search....."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div>
          <button
            className="btn"
            style={{
              backgroundColor: "#0AB7EA",
              color: "white",
              border: "none",
              padding: "8px 16px",
              borderRadius: "4px",
            }}
            onClick={handleAdd}
          >
            Add new
          </button>
        </div>
      </div>

      {loading ? (
        <Spinner />
      ) : pageFaqs.length === 0 ? (
        <p>No FAQs available.</p>
      ) : (
        <div className="accordion">
          {pageFaqs.map((item, index) => (
            <div className="accordion-item" key={item._id || index}>
              <h2 className="accordion-header">
                <button
                  className={`accordion-button ${
                    activeIndex === index ? "" : "collapsed"
                  }`}
                  type="button"
                  onClick={() =>
                    setActiveIndex(activeIndex === index ? null : index)
                  }
                >
                  {item.question}
                </button>
              </h2>
              <div
                className={`accordion-collapse collapse ${
                  activeIndex === index ? "show" : ""
                }`}
              >
                <div className="accordion-body">
                  {item.answer}
                  <div className="mt-2">
                    <button
                      className="btn btn-sm  me-2"
                      style={{ background: "#0AB7EA", color: "white" }}
                      onClick={() => handleEdit(item)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(item._id, index)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card-footer clearfix">
        <div className="d-flex justify-content-between align-items-center">
          <div className="dataTables_info" style={{ color: "#0AB7EA" }}>
            Page {currentPage * itemsPerPage + 1} to{" "}
            {totalItems > (currentPage + 1) * itemsPerPage
              ? (currentPage + 1) * itemsPerPage
              : totalItems}{" "}
            of {totalItems} entries
          </div>
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <Pagination
              pageCount={totalPages}
              pageValue={currentPage}
              setPage={setCurrentPage}
            />
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="titleColor">
            {selectedFaq ? "Edit FAQ" : "Add FAQ"}
          </Modal.Title>
        </Modal.Header>
        <Formik
          enableReinitialize
          initialValues={{
            question: selectedFaq?.question || "",
            answer: selectedFaq?.answer || "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <FormikForm>
              <Modal.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Question</Form.Label>
                  <Field name="question" className="form-control" />
                  <div className="text-danger">
                    <ErrorMessage name="question" />
                  </div>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Answer</Form.Label>
                  <Field
                    as="textarea"
                    name="answer"
                    className="form-control"
                    rows={3}
                  />
                  <div className="text-danger">
                    <ErrorMessage name="answer" />
                  </div>
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  style={{ backgroundColor: "#0AB7EA", border: "none" }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "updating" : "Save Changes"}
                </Button>
              </Modal.Footer>
            </FormikForm>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

export default LegalInfo;
